import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';

import { cn } from '@/nucleo/utilitarios';

interface UpdatedAtProps {
  /** Timestamp (ms) — geralmente `query.dataUpdatedAt` do React Query. */
  updatedAt?: number | null;
  /** Indica revalidação em andamento (spin do ícone + rótulo "Atualizando…"). */
  isFetching?: boolean;
  /** Ação de atualização manual. Sem isto, só o rótulo aparece (sem botão). */
  onRefresh?: () => void;
  className?: string;
}

/**
 * Pílula "atualizado agora"/"atualizado às HH:mm", com botão de atualizar
 * embutido. Cópia estrutural do protótipo (`UpdatedAt.tsx`, referência fora
 * do git deste repo) — padrão visual único pra cabeçalho de dashboard.
 */
export function UpdatedAt({ updatedAt, isFetching, onRefresh, className }: UpdatedAtProps) {
  const [, forcarRenderizacao] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => forcarRenderizacao((n) => n + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const rotulo = (() => {
    if (isFetching) return 'Atualizando…';
    if (!updatedAt) return 'Aguardando dados';
    const segundos = Math.round((Date.now() - updatedAt) / 1000);
    if (segundos < 60) return 'Atualizado agora';
    return `Atualizado às ${new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(new Date(updatedAt))}`;
  })();

  const classeBase =
    'inline-flex h-7 items-center gap-1.5 rounded-full border border-border bg-muted/60 px-2.5 text-[11px] font-medium text-muted-foreground';

  if (!onRefresh) {
    return (
      <span className={cn(classeBase, className)} aria-live="polite">
        <RefreshCw className={cn('h-3 w-3', isFetching && 'animate-spin')} aria-hidden="true" />
        {rotulo}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onRefresh}
      disabled={isFetching}
      title="Atualizar agora"
      aria-live="polite"
      className={cn(
        classeBase,
        'transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60',
        className,
      )}
    >
      <RefreshCw className={cn('h-3 w-3', isFetching && 'animate-spin')} aria-hidden="true" />
      {rotulo}
    </button>
  );
}

export default UpdatedAt;
